import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  AppointmentFilter,
  IAppointment,
  MajorCategories,
  TimeSlot,
} from '@alpha/types';
import {
  BodyScanAppointmentService,
  ClinicAppointmentService,
  UIUtilService,
} from 'app-service';
import {
  AppLogger,
  Button,
  FilterType,
  ICardGroupItem,
  ICardItem,
  IFilterItem,
  RadioButton,
  TranformService,
} from 'app-core';

import ButtonType = Data.Core.ButtonType;
import CardType = Data.Core.CardType;
import IconType = Data.Core.IconType;
import ShimmerType = Data.Core.ShimmerType;
import { environment } from 'src/environments/environment';

interface ICardItems extends ICardItem {
  clinicId: string;
  doctorId: string;
}
@Component({
  selector: 'app-list-appointments',
  templateUrl: './list-appointments.component.html',
  styleUrls: ['./list-appointments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListAppointmentsComponent implements OnInit, OnDestroy {
  public isListEmpty: boolean = true;
  private loadMore: boolean = false;
  private _clinicAppointmentPageCounter: number = 1;
  private _bodyScanAppointmentpageCounter: number = 1;
  public loadMoreButton: Button = new Button({
    label: 'Load More Results',
    type: ButtonType.button,
    action: () => {
      if (!this.isListEmpty) {
        this.loadMore = true;

        if (
          Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
          Number(MajorCategories.Doctor)
        ) {
          this._clinicAppointmentPageCounter =
            this._clinicAppointmentPageCounter + 1;
          this._getDoctorsAppointmentList(
            this._clinicAppointmentPageCounter,
            this.selectedTimeFilter,
          );
        }

        if (
          Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
          Number(MajorCategories.BodyScan)
        ) {
          this._bodyScanAppointmentpageCounter =
            this._bodyScanAppointmentpageCounter + 1;
          this._getBodyScanAppointmentList(
            this._bodyScanAppointmentpageCounter,
            this.selectedTimeFilter,
          );
        }
      }
    },
  });
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  private selectedTimeFilter: AppointmentFilter = AppointmentFilter.Future;
  private selectedCategoryFilter!: MajorCategories;
  public radioFilter: FilterType = 'RadioButton';
  public loading: boolean = false;
  public cards: Array<ICardGroupItem> = [];
  public noAppointment: boolean = true;
  public filterItems: Array<IFilterItem> = [];
  public showFiltersButton: Button = new Button({
    label: 'Filters',
    type: ButtonType.flatButton,
    icon: IconType.filterIcon,
    action: () => this._showFilters(),
  });
  public shimmerTypeSMCard: ShimmerType = ShimmerType.SMCard;
  public categoryFilterItems: Array<RadioButton> = [
    {
      label: `Doctors`,
      value: MajorCategories.Doctor,
      checked:
        Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
        Number(MajorCategories.Doctor)
          ? true
          : false,
    },
    {
      label: `Body Scan`,
      value: MajorCategories.BodyScan,
      checked:
        Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
        Number(MajorCategories.BodyScan)
          ? true
          : false,
    },
  ];
  public timeFilterItems: Array<RadioButton> = [
    {
      label: `Upcomming Appointment(s)`,
      value: AppointmentFilter.Future,
      checked:
        Number(this._activatedRoute.snapshot.queryParamMap.get('atf')) ===
        Number(MajorCategories.BodyScan)
          ? true
          : false,
    },
    {
      label: `Past Appointment(s)`,
      value: AppointmentFilter.Past,
      checked:
        Number(this._activatedRoute.snapshot.queryParamMap.get('atf')) ===
        Number(MajorCategories.BodyScan)
          ? true
          : false,
    },
  ];
  private listObj: Array<ICardItems> = [];

  public constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _appointmentService: ClinicAppointmentService,
    private readonly _bodyScanAppointmentService: BodyScanAppointmentService,
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _tranformService: TranformService,
    private readonly _router: Router,
    private readonly _uiUtilService: UIUtilService,
  ) {}

  public ngOnInit() {
    this._addFilters();

    this._uiUtilService.isNavbarOpened.next(false);

    this._activatedRoute.queryParamMap
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const url = new URL(this._router.url, environment.baseURL);
        this.selectedCategoryFilter = Number(url.searchParams.get('acf'));
        if (this.selectedCategoryFilter === Number(MajorCategories.Doctor)) {
          this._router.navigate(
            [], // Passing an empty array keeps you on the current route path
            {
              relativeTo: this._activatedRoute,
              queryParams: { acf: MajorCategories.Doctor }, // Your new parameters
              queryParamsHandling: 'merge', // 'merge' keeps existing params, use '' to overwrite
            },
          );
          this._getDoctorsAppointmentList(
            this._clinicAppointmentPageCounter,
            this.selectedTimeFilter,
          );
        } else {
          this._router.navigate(
            [], // Passing an empty array keeps you on the current route path
            {
              relativeTo: this._activatedRoute,
              queryParams: { acf: MajorCategories.BodyScan }, // Your new parameters
              queryParamsHandling: 'merge', // 'merge' keeps existing params, use '' to overwrite
            },
          );
          this._getBodyScanAppointmentList(
            this._bodyScanAppointmentpageCounter,
            this.selectedTimeFilter,
          );
        }
      });

    this._cdRef.markForCheck();
  }

  private _getDoctorsAppointmentList(
    pageCounter: number,
    selectedTimeFilter: AppointmentFilter,
  ): void {
    this.loading = true;
    this._appointmentService
      .findAllAppointmentsByFilter(pageCounter, selectedTimeFilter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (list: Array<Partial<IAppointment>>) => {
          this.isListEmpty = list.length < 10 ? true : false;
          if (list.length > 0) {
            this.noAppointment = false;

            if (!this.loadMore) {
              this.listObj = [];
            }

            list.forEach((doctor: Partial<IAppointment>) => {
              const timestamp = doctor!.date
                ? new Date(doctor.date)
                : new Date();
              const options: Intl.DateTimeFormatOptions = {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              };
              const formattedDate = timestamp.toLocaleDateString(
                'en-GB',
                options,
              );
              const time = this._tranformService.getEnumKeyByValue(
                TimeSlot,
                doctor.time || 0,
              );

              this.listObj.push({
                _id: doctor._id || '',
                title:
                  `Dr. ${doctor.doctorId && doctor.doctorId.userId && doctor.doctorId.userId.firstName} ${doctor.doctorId && doctor.doctorId.userId && doctor.doctorId.userId.lastName}` ||
                  '',
                subTitle: '',
                picture:
                  doctor.doctorId?.userId && doctor.doctorId?.userId.picture,
                rating: {
                  id: doctor.doctorId?.rating?.id,
                  by: doctor.doctorId?.rating?.by,
                },
                verified: doctor.doctorId?.verified?.verified,
                online:
                  doctor.doctorId?.clinic && doctor.doctorId?.clinic!.length > 0
                    ? doctor!.doctorId!.clinic[0]!.online
                    : 0,
                fee:
                  doctor.doctorId?.clinic && doctor.doctorId?.clinic!.length > 0
                    ? doctor.doctorId?.clinic[0].fees
                    : 0,
                type: MajorCategories.Doctor,
                appointment: {
                  date: formattedDate,
                  time: time || '',
                },
                iconList: true,
                onArrowClick: (item: any) => this.onArrowClick(item),
                clinicId: doctor.clinicId!.toString() || '',
                doctorId: doctor.doctorId!._id!.toString() || '',
              });
            });
            this.cards = [
              {
                items: this.listObj,
                type: CardType.sm,
              },
            ];
          } else {
            this.noAppointment = true;
            this.cards = [
              {
                items: [],
                type: CardType.sm,
              },
            ];
          }
        },
        (error) => {
          AppLogger.Error(error);
          this.loading = false;
        },
        () => {
          this.loading = false;
          this._cdRef.markForCheck();
        },
      );
  }

  private _getBodyScanAppointmentList(
    pageCounter: number,
    selectedTimeFilter: AppointmentFilter,
  ): void {
    this.loading = true;
    this._bodyScanAppointmentService
      .findAllAppointmentsByFilter(pageCounter, selectedTimeFilter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (list: Array<Partial<IAppointment>>) => {
          this.isListEmpty = list.length < 10 ? true : false;
          if (list.length > 0) {
            this.noAppointment = false;

            if (!this.loadMore) {
              this.listObj = [];
            }

            list.forEach((doctor: Partial<IAppointment>) => {
              const timestamp = doctor!.date
                ? new Date(doctor.date)
                : new Date();
              const options: Intl.DateTimeFormatOptions = {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              };
              const formattedDate = timestamp.toLocaleDateString(
                'en-GB',
                options,
              );
              const time = this._tranformService.getEnumKeyByValue(
                TimeSlot,
                doctor.time || 0,
              );

              this.listObj.push({
                _id: doctor._id || '',
                title:
                  `Dr. ${doctor.doctorId && doctor.doctorId.userId && doctor.doctorId.userId.firstName} ${doctor.doctorId && doctor.doctorId.userId && doctor.doctorId.userId.lastName}` ||
                  '',
                subTitle: '',
                picture:
                  doctor.doctorId?.userId && doctor.doctorId?.userId.picture,
                rating: {
                  id: doctor.doctorId?.rating?.id,
                  by: doctor.doctorId?.rating?.by,
                },
                verified: doctor.doctorId?.verified?.verified,
                online:
                  doctor.doctorId?.clinic && doctor.doctorId?.clinic!.length > 0
                    ? doctor!.doctorId!.clinic[0]!.online
                    : 0,
                fee:
                  doctor.doctorId?.clinic && doctor.doctorId?.clinic!.length > 0
                    ? doctor.doctorId?.clinic[0].fees
                    : 0,
                type: MajorCategories.Doctor,
                appointment: {
                  date: formattedDate,
                  time: time || '',
                },
                iconList: true,
                onArrowClick: (item: any) => this.onArrowClick(item),
                clinicId: doctor.clinicId!.toString() || '',
                doctorId: doctor.doctorId!._id!.toString() || '',
              });
            });
            this.cards = [
              {
                items: this.listObj,
                type: CardType.sm,
              },
            ];
          } else {
            this.noAppointment = true;
            this.cards = [
              {
                items: [],
                type: CardType.sm,
              },
            ];
          }
        },
        (error) => {
          AppLogger.Error(error);
          this.loading = false;
        },
        () => {
          this.loading = false;
          this._cdRef.markForCheck();
        },
      );
  }

  private onArrowClick(item: any): void {
    const route = `/appointment/view/appointment/${item.doctorId}/${item.clinicId}/${item._id}`;
    this._router
      .navigate([route], { queryParamsHandling: 'preserve' })
      .catch((err: string | Error) => AppLogger.Error(err));
  }

  private _showFilters(): void {
    this._uiUtilService.isNavbarOpened.next(true);
  }

  private _addFilters(): void {
    this.filterItems.push({
      title: 'Filter By Category',
      filters: {
        list: () => [...this.categoryFilterItems],
        type: this.radioFilter,
      },
      onItemSelected: (id: any) => {
        this.listObj = [];
        this.selectedCategoryFilter = id.value;
        this._router.navigate(
          [], // Passing an empty array keeps you on the current route path
          {
            relativeTo: this._activatedRoute,
            queryParams: { acf: this.selectedCategoryFilter }, // Your new parameters
            queryParamsHandling: 'merge', // 'merge' keeps existing params, use '' to overwrite
          },
        );
      },
      selectedItem:
        Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
        Number(MajorCategories.BodyScan)
          ? this.categoryFilterItems[1].value
          : this.categoryFilterItems[0].value,
      expanded: true,
    });
    this.filterItems.push({
      title: 'Filter By Date',
      filters: {
        list: () => this.timeFilterItems,
        type: this.radioFilter,
      },
      onItemSelected: (id: any) => {
        this.listObj = [];
        this.selectedTimeFilter = id.value;
        this._router.navigate(
          [], // Passing an empty array keeps you on the current route path
          {
            relativeTo: this._activatedRoute,
            queryParams: { atf: this.selectedTimeFilter }, // Your new parameters
            queryParamsHandling: 'merge', // 'merge' keeps existing params, use '' to overwrite
          },
        );
      },
      selectedItem: this.selectedTimeFilter,
      expanded: true,
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
